import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip'
import { getEstateStats } from '@/estate/utils/getEstateStats'
import { formatCurrency } from '@almaris/shared/utils/formatCurrency'
import { EstateForSalePageFragment } from '@gql'
import {
  CalendarCheckIcon,
  CoinsIcon,
  FileTextIcon,
  CircleGaugeIcon,
  InfoIcon,
} from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'

type ApplicationsStatsBlockProps = {
  estate: Pick<
    EstateForSalePageFragment,
    | 'id'
    | 'firstPrice'
    | 'applications'
    | 'estateRounds'
    | 'currentRoundId'
    | 'visits'
  >
}
export const ApplicationsStatsBlock = ({
  estate,
}: ApplicationsStatsBlockProps) => {
  const { t } = useTranslation()

  const { currentRoundId } = estate

  const currentRound = estate.estateRounds.find(
    (round) => round.id === currentRoundId
  )

  const { nbPlannedVisits, nbDoneVisits, nbDrafts, nbSubmissions } =
    getEstateStats(estate)

  return (
    <div className="tw-p-8 tw-flex tw-gap-6 tw-items-center tw-justify-between">
      <div className="tw-w-full tw-flex tw-gap-2 tw-bg-green-100 tw-rounded-lg tw-p-4 tw-items-center">
        <CoinsIcon />
        <div>
          <p className="tw-text-2xl tw-font-medium">
            {formatCurrency(estate.firstPrice ?? 0)} {t('common.FAI')}
          </p>
          <p>{t('ApplicationsStatsBlock.firstPrice')}</p>
        </div>
      </div>

      <div className="tw-w-full tw-flex tw-gap-2 tw-bg-green-100 tw-rounded-lg tw-p-4 tw-items-center">
        <CalendarCheckIcon />
        <div>
          <p>
            <span className="tw-text-2xl tw-font-medium">{nbDoneVisits}</span>/
            {nbPlannedVisits + nbDoneVisits}
          </p>
          <p>{t('ApplicationsStatsBlock.done', { count: nbDoneVisits })}</p>
        </div>
      </div>

      <div className="tw-w-full tw-flex tw-gap-2 tw-bg-green-100 tw-rounded-lg tw-p-4 tw-items-center">
        <FileTextIcon />
        <div>
          <p>
            <span className="tw-text-2xl tw-font-medium">{nbSubmissions}</span>/
            {nbDrafts + nbSubmissions}
          </p>
          <p>
            {t('ApplicationsStatsBlock.received', {
              count: nbSubmissions,
            })}
          </p>
        </div>
      </div>
      {currentRound?.number && currentRound.number > 1 && (
        <div className="tw-w-full tw-flex tw-gap-2 tw-bg-amber-100 tw-rounded-lg tw-p-4 tw-items-center">
          <CircleGaugeIcon />
          <div>
            <p>
              <span className="tw-text-2xl tw-font-medium">
                Nº{currentRound.number}
              </span>
              {currentRound.isPublic && (
                <span className="tw-ml-2 tw-rounded-md tw-bg-emerald-200 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-uppercase tw-text-emerald-800">
                  {t('ApplicationsStatsBlock.newRound.publicBadge')}
                </span>
              )}
            </p>
            <p>
              {t('ApplicationsStatsBlock.newRound.title')}{' '}
              <TooltipProvider delayDuration={400}>
                <Tooltip>
                  <TooltipTrigger>
                    <InfoIcon className="tw-ml-2 tw-h-[14px] tw-w-[14px]" />
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('ApplicationsStatsBlock.newRound.tooltip', {
                      number: currentRound.number,
                      count: currentRound.estateRoundBuyerMembers.length,
                    })}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
